/**
 * features/admissions/AdmissionsModule.tsx
 * ------------------------------------------------------------------
 * Feature E — Admissions Module.
 *
 * Standalone screen for student intake. This is the canonical place
 * for full student/parent data including guardians, medical info,
 * and administrative details. Designed to work as the ONLY screen
 * an `admissions` role user sees.
 *
 * Tabbed form: Personal → Guardians → Medical → Administrative.
 * Auto-generates admission numbers (LKC prefix, incrementing).
 * Saves to the existing `reportStudents` collection, adding the
 * `admissions` sub-object alongside existing fields.
 *
 * Gated by `canAccessAdmissions` permission.
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import {
  collection,
  doc,
} from 'firebase/firestore';
// Batch D: undefined-safe write wrapper
// AdmissionsModule already passes its payloads through stripUndefined
// explicitly. This import swap is defence-in-depth, no behaviour change.
import { addDoc, updateDoc } from '@/lib/firebase';
import {
  UserPlus,
  Users,
  Search,
  Pencil,
  ChevronRight,
  ChevronLeft,
  Save,
  Plus,
  Trash2,
  Star,
  Heart,
  Stethoscope,
  ClipboardList,
  ClipboardCheck,
  AlertCircle,
  CheckCircle2,
  Settings,
  FileText,
  User,
  Phone,
  Mail,
  MessageCircle,
  Briefcase,
  Building2,
  MapPin,
  Calendar,
  Globe,
  BookOpen,
  Home,
  Shield,
  Camera,
  Loader2,
  X,
  Share2,
  Clock,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import ApplicationsList from '@/features/applications/ApplicationsList';
import ApplicationDetail from '@/features/applications/ApplicationDetail';
import AdmissionWindowPanel from '@/features/admissions/AdmissionWindowPanel';
import ShareApplicationLink from '@/features/admissions/ShareApplicationLink';
import PendingList from '@/features/admissions/PendingList';
import DocumentsTab from '@/features/documents/DocumentsTab';
import {
  sanitizeText,
  sanitizeEmail,
  sanitizePhone,
  stripUndefined,
} from '@/lib/sanitize';
import {
  getNextAdmissionNumber,
  isAdmissionNumberTaken,
  validateGuardian,
  validatePrimaryContact,
  GUARDIAN_ROLE_LABELS,
} from '@/lib/admissions';
import {
  validatePhone,
  validateEmail,
  validateIdNumber,
  validateDateOfBirth,
} from '@/lib/validation';
import {
  uploadStudentPhoto,
  removeStudentPhoto,
  useStudentPhotoUrl,
} from '@/lib/studentPhoto';
import type { StudentPhoto } from '@/lib/studentPhotoPolicy';
import PhotoThumbPreview from '@/components/ui/PhotoThumbPreview';
import StudentAvatar from '@/components/ui/StudentAvatar';
import { Spinner } from '@/components/ui/primitives';
import {
  GradientHero,
  SoftCard,
  StatChip,
  AccentButton,
  glassFieldClass,
} from '@/features/finance/ui';
import type {
  Student,
  Guardian,
  GuardianRole,
  AdmissionsData,
} from '@/types';

/* ==================================================================
   TYPES & CONSTANTS
   ================================================================== */

type Tab = 'personal' | 'guardians' | 'medical' | 'administrative' | 'entrance' | 'documents';

const TABS: { id: Tab; label: string; icon: typeof User }[] = [
  { id: 'personal', label: 'Personal', icon: User },
  { id: 'guardians', label: 'Guardians', icon: Users },
  { id: 'medical', label: 'Medical', icon: Stethoscope },
  { id: 'administrative', label: 'Administrative', icon: ClipboardList },
  { id: 'entrance', label: 'Entrance Test', icon: ClipboardCheck },
  { id: 'documents', label: 'Documents', icon: FileText },
];

const GENDER_OPTIONS: { value: 'M' | 'F'; label: string }[] = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

const BOARDING_OPTIONS: { value: 'day' | 'boarding'; label: string }[] = [
  { value: 'day', label: 'Day Scholar' },
  { value: 'boarding', label: 'Boarding' },
];

const GUARDIAN_ROLES: { value: GuardianRole; label: string }[] = Object.entries(
  GUARDIAN_ROLE_LABELS,
).map(([value, label]) => ({ value: value as GuardianRole, label }));

interface PersonalForm {
  firstName: string;
  surname: string;
  gender: 'M' | 'F' | ''; // '' = not yet chosen (placeholder); required before save
  dateOfBirth: string;
  admissionNumber: string;
  nationality: string;
  homeLanguage: string;
  religion: string;
  address: string;
  currentClass: string;
}

interface MedicalForm {
  doctorName: string;
  doctorPhone: string;
  medicalAidProvider: string;
  medicalAidNumber: string;
  allergies: string;
  chronicConditions: string;
  medications: string;
  bloodType: string;
}

interface AdminForm {
  previousSchool: string;
  dateOfAdmission: string;
  boardingStatus: 'day' | 'boarding';
  notes: string;
}

/** One row in the entrance-test panel. Marks are held as strings while
 *  editing (number inputs) and parsed to numbers on save. */
interface EntranceRow {
  subjectId: string;
  mark: string;
  outOf: string;
}
interface EntranceForm {
  taken: boolean;
  testDate: string;
  notes: string;
  results: EntranceRow[];
}

function emptyGuardian(): Guardian {
  return {
    id: crypto.randomUUID().slice(0, 12),
    role: 'father',
    roleLabel: '',
    fullName: '',
    idNumber: '',
    occupation: '',
    employer: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    isPrimaryContact: false,
    consentToSend: false,
    consentToWhatsapp: false,
  };
}

const EMPTY_PERSONAL: PersonalForm = {
  firstName: '',
  surname: '',
  gender: 'M',
  dateOfBirth: '',
  admissionNumber: '',
  nationality: 'Motswana',
  homeLanguage: 'Setswana',
  religion: '',
  address: '',
  currentClass: '',
};

const EMPTY_MEDICAL: MedicalForm = {
  doctorName: '',
  doctorPhone: '',
  medicalAidProvider: '',
  medicalAidNumber: '',
  allergies: '',
  chronicConditions: '',
  medications: '',
  bloodType: '',
};

const EMPTY_ADMIN: AdminForm = {
  previousSchool: '',
  dateOfAdmission: new Date().toISOString().slice(0, 10),
  boardingStatus: 'day',
  notes: '',
};

const EMPTY_ENTRANCE: EntranceForm = {
  taken: false,
  testDate: '',
  notes: '',
  results: [],
};

/* ==================================================================
   COMPONENT
   ================================================================== */

export default function AdmissionsModule({ editStudentId }: { editStudentId?: string }) {
  const { students, classes, subjects, loading: dataLoading } = useData();
  const { userData } = useAuth();

  // Mode: list (default students list) or form (new / edit) or applications (review tab) or settings
  const [mode, setMode] = useState<'list' | 'form' | 'applicationsList' | 'applicationDetail' | 'settings' | 'shareLink' | 'pendingList'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [openApplicationId, setOpenApplicationId] = useState<string | null>(null);

  // Auto-open edit form when navigated from Students tab with a student ID
  useEffect(() => {
    if (editStudentId && students.length > 0) {
      const student = students.find((s) => s.id === editStudentId);
      if (student) {
        openEdit(student);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editStudentId, students.length]);

  // Form state
  const [tab, setTab] = useState<Tab>('personal');
  const [personal, setPersonal] = useState<PersonalForm>(EMPTY_PERSONAL);
  const [guardians, setGuardians] = useState<Guardian[]>([emptyGuardian()]);
  const [medical, setMedical] = useState<MedicalForm>(EMPTY_MEDICAL);
  const [admin, setAdmin] = useState<AdminForm>(EMPTY_ADMIN);
  const [entrance, setEntrance] = useState<EntranceForm>(EMPTY_ENTRANCE);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Photo state
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  /* The pupil's current photo, fetched through the storage rules. There is
     no stored link to show any more — see lib/studentPhoto. */
  const [existingPhoto, setExistingPhoto] = useState<StudentPhoto | null>(null);
  const existingPhotoUrl = useStudentPhotoUrl(existingPhoto, 'full');
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // List state
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');

  // Auto-set admission number for new students
  useEffect(() => {
    if (mode === 'form' && !editingId && !personal.admissionNumber) {
      setPersonal((p) => ({
        ...p,
        admissionNumber: getNextAdmissionNumber(students),
      }));
    }
  }, [mode, editingId, students, personal.admissionNumber]);

  // Mark first guardian as primary if none are
  useEffect(() => {
    if (guardians.length > 0 && !guardians.some((g) => g.isPrimaryContact)) {
      setGuardians((gs) => gs.map((g, i) => ({ ...g, isPrimaryContact: i === 0 })));
    }
  }, [guardians.length]); // only on count change, not every render

  const sortedStudents = useMemo(() => {
    const q = search.toLowerCase();
    return [...students]
      .filter(
        (s) =>
          (!classFilter || s.currentClass === classFilter) &&
          (s.fullName.toLowerCase().includes(q) ||
            s.admissionNumber?.toLowerCase().includes(q) ||
            s.admissions?.firstName?.toLowerCase().includes(q) ||
            s.admissions?.surname?.toLowerCase().includes(q)),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [students, search, classFilter]);

  const className = (id: string) =>
    classes.find((c) => c.id === id)?.name ?? id;

  const stats = useMemo(() => {
    const withAdmissions = students.filter((s) => s.admissions).length;
    const withGuardians = students.filter(
      (s) => s.admissions?.guardians?.length,
    ).length;
    const withMedical = students.filter(
      (s) =>
        s.admissions?.medical &&
        (s.admissions.medical.doctorName || s.admissions.medical.medicalAidNumber),
    ).length;
    return { total: students.length, withAdmissions, withGuardians, withMedical };
  }, [students]);

  /* ---- OPEN FORM (new or edit) ----------------------------------- */

  function openNew() {
    setEditingId(null);
    setPersonal({ ...EMPTY_PERSONAL, admissionNumber: getNextAdmissionNumber(students) });
    const first = emptyGuardian();
    first.isPrimaryContact = true;
    setGuardians([first]);
    setMedical({ ...EMPTY_MEDICAL });
    setAdmin({ ...EMPTY_ADMIN });
    setEntrance({ ...EMPTY_ENTRANCE, results: [] });
    setPhotoPreview(null);
    setPhotoFile(null);
    setExistingPhoto(null);
    setPhotoDeleted(false);
    setTab('personal');
    setMsg(null);
    setMode('form');
  }

  function openEdit(student: Student) {
    setEditingId(student.id);
    const a = student.admissions;
    setPersonal({
      firstName: a?.firstName ?? student.fullName.split(' ')[0] ?? '',
      surname: a?.surname ?? student.fullName.split(' ').slice(1).join(' ') ?? '',
      gender: student.gender,
      dateOfBirth: a?.dateOfBirth ?? '',
      admissionNumber: student.admissionNumber ?? '',
      nationality: a?.nationality ?? '',
      homeLanguage: a?.homeLanguage ?? '',
      religion: a?.religion ?? '',
      address: a?.address ?? '',
      currentClass: student.currentClass,
    });
    if (a?.guardians?.length) {
      setGuardians(a.guardians.map((g) => ({ ...g })));
    } else if (student.parentContact) {
      // Migrate legacy contact into a guardian
      const migrated = emptyGuardian();
      migrated.fullName = student.parentContact.guardianName ?? '';
      migrated.email = student.parentContact.email ?? '';
      migrated.whatsapp = student.parentContact.whatsapp ?? '';
      migrated.isPrimaryContact = true;
      migrated.consentToSend = student.parentContact.consentToSend ?? false;
      setGuardians([migrated]);
    } else {
      const first = emptyGuardian();
      first.isPrimaryContact = true;
      setGuardians([first]);
    }
    setMedical({
      doctorName: a?.medical?.doctorName ?? '',
      doctorPhone: a?.medical?.doctorPhone ?? '',
      medicalAidProvider: a?.medical?.medicalAidProvider ?? '',
      medicalAidNumber: a?.medical?.medicalAidNumber ?? '',
      allergies: a?.medical?.allergies ?? '',
      chronicConditions: a?.medical?.chronicConditions ?? '',
      medications: a?.medical?.medications ?? '',
      bloodType: a?.medical?.bloodType ?? '',
    });
    setAdmin({
      previousSchool: a?.previousSchool ?? '',
      dateOfAdmission: a?.dateOfAdmission ?? '',
      boardingStatus: a?.boardingStatus ?? 'day',
      notes: a?.notes ?? '',
    });
    const et = a?.entranceTest;
    setEntrance(
      et
        ? {
            taken: !!et.taken,
            testDate: et.testDate ?? '',
            notes: et.notes ?? '',
            results: (et.results ?? []).map((r) => ({
              subjectId: r.subjectId,
              mark: r.mark != null ? String(r.mark) : '',
              outOf: r.outOf != null ? String(r.outOf) : '',
            })),
          }
        : { ...EMPTY_ENTRANCE, results: [] },
    );
    setTab('personal');
    setMsg(null);
    setPhotoPreview(null);
    setPhotoFile(null);
    setExistingPhoto(student.photo ?? null);
    setPhotoDeleted(false);
    setMode('form');
  }

  /* ---- PHOTO MANAGEMENT ------------------------------------------ */

  function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMsg({ ok: false, text: 'Please select an image file (JPG, PNG, etc.).' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMsg({ ok: false, text: 'Image is too large. Maximum size is 10 MB.' });
      return;
    }
    setPhotoFile(file);
    setPhotoDeleted(false);
    // Create preview
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
  }

  function handlePhotoRemove() {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoDeleted(true);
    if (photoInputRef.current) photoInputRef.current.value = '';
  }

  /* ---- GUARDIAN MANAGEMENT --------------------------------------- */

  function addGuardian() {
    const g = emptyGuardian();
    if (guardians.length === 0) g.isPrimaryContact = true;
    setGuardians((gs) => [...gs, g]);
  }

  function removeGuardian(idx: number) {
    setGuardians((gs) => {
      const next = gs.filter((_, i) => i !== idx);
      // If we removed the primary, promote the first remaining
      if (next.length > 0 && !next.some((g) => g.isPrimaryContact)) {
        next[0].isPrimaryContact = true;
      }
      return next;
    });
  }

  function updateGuardian(idx: number, patch: Partial<Guardian>) {
    setGuardians((gs) =>
      gs.map((g, i) => {
        if (i !== idx) {
          // If setting this one as primary, clear others
          if (patch.isPrimaryContact) return { ...g, isPrimaryContact: false };
          return g;
        }
        return { ...g, ...patch };
      }),
    );
  }

  /* ---- SAVE ----------------------------------------------------- */

  async function handleSave() {
    // Validate
    const errors: string[] = [];

    if (!personal.firstName.trim()) errors.push('First name is required.');
    if (!personal.surname.trim()) errors.push('Surname is required.');
    if (!personal.currentClass) errors.push('Class is required.');
    if (!personal.admissionNumber.trim()) errors.push('Admission number is required.');

    if (
      isAdmissionNumberTaken(personal.admissionNumber, students, editingId ?? undefined)
    ) {
      errors.push(`Admission number ${personal.admissionNumber} is already taken.`);
    }

    // Date of birth — same rules as the public form (age 5–25, valid date)
    if (personal.dateOfBirth) {
      const dobErr = validateDateOfBirth(personal.dateOfBirth, { minAge: 4, maxAge: 25 });
      if (dobErr) errors.push(dobErr);
    }

    // Validate guardians — basic rules from admissions.ts PLUS the
    // Botswana phone / E.164 / email-format rules from validation.ts.
    const primaryErr = validatePrimaryContact(guardians);
    if (primaryErr) errors.push(primaryErr);
    for (let i = 0; i < guardians.length; i++) {
      const g = guardians[i];
      const gErrors = validateGuardian(g);
      for (const e of gErrors) {
        errors.push(`Guardian ${i + 1}: ${e.message}`);
      }
      // Strict phone / email / ID checks
      const phoneErr = validatePhone(g.phone);
      if (phoneErr) errors.push(`Guardian ${i + 1} phone: ${phoneErr}`);
      const waErr = validatePhone(g.whatsapp);
      if (waErr) errors.push(`Guardian ${i + 1} WhatsApp: ${waErr}`);
      const emailErr = validateEmail(g.email);
      if (emailErr) errors.push(`Guardian ${i + 1} email: ${emailErr}`);
      const idErr = validateIdNumber(g.idNumber);
      if (idErr) errors.push(`Guardian ${i + 1} ID: ${idErr}`);
      // Primary contact must have at least one reach channel
      if (g.isPrimaryContact && !g.phone && !g.whatsapp && !g.email) {
        errors.push(
          `Guardian ${i + 1} is the primary contact and must have at least one of phone, WhatsApp, or email.`,
        );
      }
    }

    // Medical phone (doctor) — if filled, must be valid
    const doctorPhoneErr = validatePhone(medical.doctorPhone);
    if (doctorPhoneErr) errors.push(`Doctor phone: ${doctorPhoneErr}`);

    if (errors.length) {
      setMsg({ ok: false, text: errors.join(' ') });
      return;
    }

    setSaving(true);
    setMsg(null);

    try {
      const fullName = `${sanitizeText(personal.firstName.trim())} ${sanitizeText(personal.surname.trim())}`;
      const now = new Date().toISOString();
      const actor = userData?.uid ?? 'system';

      const admissionsData: AdmissionsData = {
        firstName: sanitizeText(personal.firstName.trim(), 80),
        surname: sanitizeText(personal.surname.trim(), 80),
        nationality: sanitizeText(personal.nationality.trim(), 80) || undefined,
        homeLanguage: sanitizeText(personal.homeLanguage.trim(), 80) || undefined,
        religion: sanitizeText(personal.religion.trim(), 80) || undefined,
        address: sanitizeText(personal.address.trim(), 200) || undefined,
        guardians: guardians.map((g) => ({
          id: g.id,
          role: g.role,
          roleLabel: g.role === 'other' ? sanitizeText(g.roleLabel ?? '', 60) : undefined,
          fullName: sanitizeText(g.fullName.trim(), 100),
          idNumber: sanitizeText(g.idNumber ?? '', 30) || undefined,
          occupation: sanitizeText(g.occupation ?? '', 80) || undefined,
          employer: sanitizeText(g.employer ?? '', 80) || undefined,
          phone: sanitizePhone(g.phone) || undefined,
          whatsapp: sanitizePhone(g.whatsapp) || undefined,
          email: sanitizeEmail(g.email) || undefined,
          address: sanitizeText(g.address ?? '', 200) || undefined,
          isPrimaryContact: g.isPrimaryContact,
          consentToSend: g.consentToSend ?? false,
          consentToWhatsapp: g.consentToWhatsapp ?? false,
        })),
        medical:
          medical.doctorName ||
          medical.medicalAidNumber ||
          medical.allergies ||
          medical.chronicConditions
            ? {
                doctorName: sanitizeText(medical.doctorName, 100) || undefined,
                doctorPhone: sanitizePhone(medical.doctorPhone) || undefined,
                medicalAidProvider:
                  sanitizeText(medical.medicalAidProvider, 80) || undefined,
                medicalAidNumber:
                  sanitizeText(medical.medicalAidNumber, 40) || undefined,
                allergies: sanitizeText(medical.allergies, 500) || undefined,
                chronicConditions:
                  sanitizeText(medical.chronicConditions, 500) || undefined,
                medications: sanitizeText(medical.medications, 500) || undefined,
                bloodType: sanitizeText(medical.bloodType, 10) || undefined,
              }
            : undefined,
        previousSchool: sanitizeText(admin.previousSchool, 100) || undefined,
        dateOfAdmission: admin.dateOfAdmission || undefined,
        boardingStatus: admin.boardingStatus,
        notes: sanitizeText(admin.notes, 1000) || undefined,
        entranceTest: entrance.taken
          ? {
              taken: true,
              testDate: entrance.testDate || undefined,
              notes: sanitizeText(entrance.notes, 1000) || undefined,
              results: entrance.results
                .filter((r) => r.subjectId && r.mark.trim() !== '')
                .map((r) => ({
                  subjectId: r.subjectId,
                  mark: Number(r.mark),
                  ...(r.outOf.trim() !== '' ? { outOf: Number(r.outOf) } : {}),
                })),
            }
          : undefined,
        updatedAt: now,
        updatedBy: actor,
      };

      /* The photo is no longer part of `admissions`. It lives in a top-level
         `photo` field only the server writes, so this save can neither wipe
         it nor forge it. New photos and removals are handled after the
         record is saved, below. */

      // Build the primary contact for backward compatibility
      const primary = guardians.find((g) => g.isPrimaryContact) ?? guardians[0];
      const parentContact = {
        guardianName: sanitizeText(primary?.fullName ?? '', 80),
        email: sanitizeEmail(primary?.email) || undefined,
        whatsapp: sanitizePhone(primary?.whatsapp) || undefined,
        consentToSend: primary?.consentToSend ?? false,
      };

      let savedStudentId = editingId;

      if (editingId) {
        // Update existing student
        await updateDoc(doc(db, COLLECTIONS.students, editingId), stripUndefined({
          fullName,
          gender: personal.gender,
          admissionNumber: sanitizeText(personal.admissionNumber.trim(), 20),
          currentClass: personal.currentClass,
          dateOfBirth: personal.dateOfBirth || undefined,
          admissions: admissionsData,
          parentContact,
          updatedAt: now,
          updatedBy: actor,
        }));
      } else {
        // Create new student
        const docRef = await addDoc(collection(db, COLLECTIONS.students), stripUndefined({
          fullName,
          gender: personal.gender,
          admissionNumber: sanitizeText(personal.admissionNumber.trim(), 20),
          currentClass: personal.currentClass,
          dateOfBirth: personal.dateOfBirth || undefined,
          subjects: [],
          subjectTeachers: {},
          admissions: admissionsData,
          parentContact,
          createdAt: now,
          createdBy: actor,
          updatedAt: now,
          updatedBy: actor,
        }));
        savedStudentId = docRef.id;
      }

      /* Photo, after the record exists. Sent through the same server-cleaned
         route a parent's photo takes — re-drawn, location data dropped, no
         shareable link — so there is exactly one way a photo reaches a
         pupil's record. */
      if (photoFile && savedStudentId) {
        setPhotoUploading(true);
        try {
          const outcome = await uploadStudentPhoto({
            studentId: savedStudentId, file: photoFile, uploadedBy: actor, timeoutMs: 30_000,
          });
          if (outcome.status === 'rejected') {
            setMsg({ ok: false, text: `${fullName} saved, but the photo was not accepted: ${outcome.reason}` });
            setSaving(false);
            return;
          }
          if (outcome.status === 'timeout') {
            setMsg({ ok: true, text: `${fullName} saved. The photo is still being processed and will appear shortly.` });
            setSaving(false);
            return;
          }
        } catch (photoErr) {
          setMsg({ ok: false, text: `${fullName} saved, but the photo could not be uploaded: ${(photoErr as Error).message}` });
          setSaving(false);
          return;
        } finally {
          setPhotoUploading(false);
        }
      }

      if (photoDeleted && editingId && existingPhoto) {
        try {
          await removeStudentPhoto(editingId, 'Removed from the admissions record');
        } catch (e) {
          setMsg({ ok: false, text: `${fullName} saved, but the photo could not be removed: ${(e as Error).message}` });
          setSaving(false);
          return;
        }
      }

      setMsg({
        ok: true,
        text: editingId
          ? `${fullName} updated successfully.`
          : `${fullName} admitted successfully as ${personal.admissionNumber}.`,
      });

      // Stay on form after save so they can review, but reset for new
      if (!editingId) {
        setPersonal({
          ...EMPTY_PERSONAL,
          admissionNumber: getNextAdmissionNumber([
            ...students,
            { admissionNumber: personal.admissionNumber } as Student,
          ]),
        });
        const first = emptyGuardian();
        first.isPrimaryContact = true;
        setGuardians([first]);
        setMedical({ ...EMPTY_MEDICAL });
        setAdmin({ ...EMPTY_ADMIN });
        setEntrance({ ...EMPTY_ENTRANCE, results: [] });
        setPhotoPreview(null);
        setPhotoFile(null);
        setExistingPhoto(null);
        setPhotoDeleted(false);
        setTab('personal');
      }
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /* ---- RENDER --------------------------------------------------- */

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner label="Loading…" />
      </div>
    );
  }

  // Permission check for the Applications tab
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const canReviewApplications = perms.canReviewApplications;
  // Admin/head set (mirrors rules isAdminOrHead) read from the roles
  // array, so academic admins — and anyone whose admin role lives in
  // roles[] rather than the legacy single `role` field — can manage the
  // admission window settings.
  const settingsRoles =
    userData?.roles && userData.roles.length
      ? userData.roles
      : userData?.role
        ? [userData.role]
        : [];
  const canManageWindowSettings = settingsRoles.some((r) =>
    ['admin', 'systemAdmin', 'academicAdmin', 'headteacher'].includes(r),
  );
  const isListView =
    mode === 'list' || mode === 'applicationsList' || mode === 'settings' || mode === 'shareLink' || mode === 'pendingList';

  return (
    <div className="space-y-6">
      {/* HERO */}
      <GradientHero
        icon={UserPlus}
        title="Admissions"
        subtitle={
          mode === 'form'
            ? editingId
              ? 'Editing student record'
              : 'New student admission'
            : mode === 'applicationsList'
              ? 'Review submitted applications'
              : mode === 'applicationDetail'
                ? 'Application review'
                : mode === 'settings'
                  ? 'Configure the public application form'
                  : `${stats.total} students · ${stats.withAdmissions} with full records`
        }
        actions={
          mode === 'list' ? (
            <AccentButton icon={Plus} onClick={openNew}>
              New Admission
            </AccentButton>
          ) : mode === 'form' ? (
            <AccentButton icon={ChevronLeft} variant="soft" onClick={() => setMode('list')}>
              Back to List
            </AccentButton>
          ) : null
        }
      />

      {/* TAB STRIP — visible only on the list-style views, and only when the
          user can review applications (otherwise there's only one tab so
          showing it would be pointless). */}
      {isListView && canReviewApplications && (
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setMode('list')}
            className={[
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              mode === 'list'
                ? 'border-[var(--accent-strong)] text-[var(--accent-link)] dark:text-[var(--accent-link-dark)]'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium',
            ].join(' ')}
          >
            Students
          </button>
          <button
            onClick={() => setMode('applicationsList')}
            className={[
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
              mode === 'applicationsList'
                ? 'border-[var(--accent-strong)] text-[var(--accent-link)] dark:text-[var(--accent-link-dark)]'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium',
            ].join(' ')}
          >
            <ClipboardList size={14} />
            Applications
          </button>
          <button
            onClick={() => setMode('pendingList')}
            className={[
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
              mode === 'pendingList'
                ? 'border-[var(--accent-strong)] text-[var(--accent-link)] dark:text-[var(--accent-link-dark)]'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium',
            ].join(' ')}
          >
            <Clock size={14} />
            Pending
          </button>
          <button
            onClick={() => setMode('shareLink')}
            className={[
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
              mode === 'shareLink'
                ? 'border-[var(--accent-strong)] text-[var(--accent-link)] dark:text-[var(--accent-link-dark)]'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium',
            ].join(' ')}
          >
            <Share2 size={14} />
            Share link
          </button>
          {canManageWindowSettings && (
            <button
              onClick={() => setMode('settings')}
              className={[
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
                mode === 'settings'
                  ? 'border-[var(--accent-strong)] text-[var(--accent-link)] dark:text-[var(--accent-link-dark)]'
                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium',
              ].join(' ')}
            >
              <Settings size={14} />
              Settings
            </button>
          )}
        </div>
      )}

      {mode === 'list' && renderList()}
      {mode === 'form' && renderForm()}
      {mode === 'applicationsList' && (
        <ApplicationsList
          onOpenApplication={(id) => {
            setOpenApplicationId(id);
            setMode('applicationDetail');
          }}
        />
      )}
      {mode === 'applicationDetail' && openApplicationId && (
        <ApplicationDetail
          applicationId={openApplicationId}
          onBack={() => {
            setOpenApplicationId(null);
            setMode('applicationsList');
          }}
        />
      )}
      {mode === 'pendingList' && canReviewApplications && <PendingList />}
      {mode === 'shareLink' && canReviewApplications && <ShareApplicationLink />}
      {mode === 'settings' && canManageWindowSettings && <AdmissionWindowPanel />}
    </div>
  );

  /* ================================================================
     LIST VIEW
     ================================================================ */

  function renderList() {
    return (
      <div className="space-y-6">
        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatChip tone="blue" value={stats.total} label="Total Students" icon={Users} />
          <StatChip
            tone="emerald"
            value={stats.withAdmissions}
            label="Full Records"
            icon={CheckCircle2}
          />
          <StatChip
            tone="gold"
            value={stats.withGuardians}
            label="With Guardians"
            icon={Shield}
          />
          <StatChip
            tone="purple"
            value={stats.withMedical}
            label="Medical Info"
            icon={Stethoscope}
          />
        </div>

        {/* Search + filter */}
        <SoftCard>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by name or admission number…"
                className={glassFieldClass('pl-10')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className={glassFieldClass('sm:w-48')}
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">All Classes</option>
              {classes
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
        </SoftCard>

        {/* Student list */}
        <SoftCard pad="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                  <th className="px-5 py-3 font-medium">Adm #</th>
                  <th className="px-5 py-3 font-medium">Student Name</th>
                  <th className="px-5 py-3 font-medium hidden sm:table-cell">Class</th>
                  <th className="px-5 py-3 font-medium hidden md:table-cell">Guardian</th>
                  <th className="px-5 py-3 font-medium hidden md:table-cell">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map((s) => {
                  const primary =
                    s.admissions?.guardians?.find((g) => g.isPrimaryContact) ??
                    s.admissions?.guardians?.[0];
                  const hasAdmissions = !!s.admissions;
                  return (
                    <tr
                      key={s.id}
                      className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-5 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                        {s.admissionNumber ?? '—'}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <StudentAvatar student={s} size="sm" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {s.fullName}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 hidden sm:table-cell text-gray-600 dark:text-gray-400">
                        {className(s.currentClass)}
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400">
                        {primary?.fullName ??
                          s.parentContact?.guardianName ??
                          '—'}
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell">
                        {hasAdmissions ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            <CheckCircle2 size={12} /> Complete
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            <AlertCircle size={12} /> Incomplete
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => openEdit(s)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primaryLight hover:text-primary dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Pencil size={14} />
                          {hasAdmissions ? 'Edit' : 'Complete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {sortedStudents.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-10 text-center text-gray-400"
                    >
                      {search || classFilter
                        ? 'No students match your search.'
                        : 'No students yet. Click "New Admission" to add one.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SoftCard>
      </div>
    );
  }

  /* ================================================================
     FORM VIEW
     ================================================================ */

  function renderForm() {
    // Documents tab only makes sense for existing students (need an ID).
    // Hide it from the New Admission flow.
    const formTabs = editingId ? TABS : TABS.filter((t) => t.id !== 'documents');
    // If the user was on documents and switched to new mode, reset
    const safeTab = formTabs.some((t) => t.id === tab) ? tab : 'personal';
    return (
      <div className="space-y-6">
        {/* Tab strip */}
        <SoftCard pad="p-2">
          <div className="flex gap-1 overflow-x-auto">
            {formTabs.map((t) => {
              const Icon = t.icon;
              const active = safeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
                    active
                      ? 'bg-primary text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </SoftCard>

        {/* Tab content */}
        {safeTab === 'personal' && renderPersonalTab()}
        {safeTab === 'guardians' && renderGuardiansTab()}
        {safeTab === 'medical' && renderMedicalTab()}
        {safeTab === 'administrative' && renderAdminTab()}
        {safeTab === 'entrance' && renderEntranceTab()}
        {safeTab === 'documents' && editingId && (
          <SoftCard>
            <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
              <FileText size={16} className="text-primaryLight" /> Documents
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Upload and manage documents for this student. PDF and images
              only, max 10&nbsp;MB each.
            </p>
            <DocumentsTab
              studentId={editingId}
              uploader={{ uid: userData?.uid ?? '', displayName: userData?.displayName }}
            />
          </SoftCard>
        )}

        {/* Message + save bar */}
        <SoftCard>
          {msg && (
            <div
              className={`flex items-center gap-2 mb-4 text-sm ${
                msg.ok
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-red-700 dark:text-red-300'
              }`}
            >
              {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              {msg.text}
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setMode('list')}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ← Cancel
            </button>
            <div className="flex items-center gap-3">
              {/* Tab navigation — only between the form-flow tabs, not Documents */}
              {safeTab !== 'personal' && safeTab !== 'documents' && (
                <AccentButton
                  variant="soft"
                  icon={ChevronLeft}
                  onClick={() => {
                    const flow: Tab[] = ['personal', 'guardians', 'medical', 'administrative'];
                    const idx = flow.indexOf(safeTab as Tab);
                    if (idx > 0) setTab(flow[idx - 1]);
                  }}
                >
                  Previous
                </AccentButton>
              )}
              {safeTab !== 'administrative' && safeTab !== 'documents' && (
                <AccentButton
                  variant="soft"
                  icon={ChevronRight}
                  onClick={() => {
                    const flow: Tab[] = ['personal', 'guardians', 'medical', 'administrative'];
                    const idx = flow.indexOf(safeTab as Tab);
                    if (idx >= 0 && idx < flow.length - 1) setTab(flow[idx + 1]);
                  }}
                >
                  Next
                </AccentButton>
              )}
              {safeTab !== 'documents' && (
                <AccentButton icon={Save} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Update Student' : 'Admit Student'}
                </AccentButton>
              )}
            </div>
          </div>
        </SoftCard>
      </div>
    );
  }

  /* ---- PERSONAL TAB --------------------------------------------- */

  function renderPersonalTab() {
    const displayPhoto = photoPreview || (existingPhotoUrl && !photoDeleted ? existingPhotoUrl : null);

    return (
      <SoftCard>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
          <User size={20} className="text-primaryLight" />
          Personal Information
        </h3>

        {/* Photo upload area */}
        <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
            <Camera size={14} className="text-gray-400" />
            Student Photo
          </label>
          <div className="flex items-center gap-5">
            {/* Photo preview / placeholder */}
            {displayPhoto ? (
              <div className="relative group">
                <img
                  src={displayPhoto}
                  alt="Student photo"
                  className="w-24 h-24 rounded-2xl object-cover border-2 border-gray-200 dark:border-gray-600 shadow-sm"
                />
                <button
                  type="button"
                  onClick={handlePhotoRemove}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                  title="Remove photo"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center text-gray-400 bg-gray-50 dark:bg-gray-700/30">
                <Camera size={24} />
                <span className="text-[10px] mt-1">No photo</span>
              </div>
            )}

            {/* Upload controls */}
            <div className="flex-1">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoUploading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primaryLight hover:text-primaryLight transition-colors disabled:opacity-50"
              >
                {photoUploading ? (
                  <><Loader2 size={16} className="animate-spin" /> Uploading…</>
                ) : (
                  <><Camera size={16} /> {displayPhoto ? 'Change Photo' : 'Upload Photo'}</>
                )}
              </button>
              <p className="text-xs text-gray-400 mt-1.5">
                JPG or PNG, max 10 MB. Auto-resized for storage.
              </p>
            </div>
          </div>
          {photoPreview && (
            <div className="mt-3 max-w-md">
              <PhotoThumbPreview src={photoPreview} name={personal.firstName.trim() || 'the pupil'} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="First Name *" icon={User}>
            <input
              className={glassFieldClass()}
              value={personal.firstName}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, firstName: e.target.value }))
              }
              placeholder="e.g. Lesego"
            />
          </Field>
          <Field label="Surname *" icon={User}>
            <input
              className={glassFieldClass()}
              value={personal.surname}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, surname: e.target.value }))
              }
              placeholder="e.g. Mokobi"
            />
          </Field>
          <Field label="Gender *">
            <select
              className={glassFieldClass()}
              value={personal.gender}
              onChange={(e) =>
                setPersonal((p) => ({
                  ...p,
                  gender: e.target.value as 'M' | 'F' | '',
                }))
              }
            >
              <option value="">— Select —</option>
              {GENDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date of Birth" icon={Calendar}>
            <input
              type="date"
              className={glassFieldClass()}
              value={personal.dateOfBirth}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, dateOfBirth: e.target.value }))
              }
            />
          </Field>
          <Field label="Admission Number *">
            <input
              className={glassFieldClass('font-mono')}
              value={personal.admissionNumber}
              onChange={(e) =>
                setPersonal((p) => ({
                  ...p,
                  admissionNumber: e.target.value.toUpperCase(),
                }))
              }
              placeholder="LKC200001"
            />
            <p className="text-xs text-gray-400 mt-1">
              Auto-generated. Override only if needed.
            </p>
          </Field>
          <Field label="Class *">
            <select
              className={glassFieldClass()}
              value={personal.currentClass}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, currentClass: e.target.value }))
              }
            >
              <option value="">Select class…</option>
              {classes
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Nationality" icon={Globe}>
            <input
              className={glassFieldClass()}
              value={personal.nationality}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, nationality: e.target.value }))
              }
            />
          </Field>
          <Field label="Home Language" icon={BookOpen}>
            <input
              className={glassFieldClass()}
              value={personal.homeLanguage}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, homeLanguage: e.target.value }))
              }
            />
          </Field>
          <Field label="Religion">
            <input
              className={glassFieldClass()}
              value={personal.religion}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, religion: e.target.value }))
              }
            />
          </Field>
          <Field label="Home Address" icon={MapPin} full>
            <textarea
              className={glassFieldClass('min-h-[80px]')}
              value={personal.address}
              onChange={(e) =>
                setPersonal((p) => ({ ...p, address: e.target.value }))
              }
              placeholder="Plot number, street, area, city"
            />
          </Field>
        </div>
      </SoftCard>
    );
  }

  /* ---- GUARDIANS TAB -------------------------------------------- */

  function renderGuardiansTab() {
    return (
      <div className="space-y-4">
        {guardians.map((g, idx) => (
          <SoftCard key={g.id}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                {g.isPrimaryContact ? (
                  <Star size={18} className="text-amber-500 fill-amber-500" />
                ) : (
                  <Users size={18} className="text-primaryLight" />
                )}
                Guardian {idx + 1}
                {g.isPrimaryContact && (
                  <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    Primary Contact
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {!g.isPrimaryContact && (
                  <button
                    onClick={() => updateGuardian(idx, { isPrimaryContact: true })}
                    className="text-xs text-gray-500 hover:text-amber-600 flex items-center gap-1"
                    title="Set as primary contact"
                  >
                    <Star size={14} /> Make primary
                  </button>
                )}
                {guardians.length > 1 && (
                  <button
                    onClick={() => removeGuardian(idx)}
                    className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Relationship *">
                <select
                  className={glassFieldClass()}
                  value={g.role}
                  onChange={(e) =>
                    updateGuardian(idx, { role: e.target.value as GuardianRole })
                  }
                >
                  {GUARDIAN_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              {g.role === 'other' && (
                <Field label="Specify Relationship *">
                  <input
                    className={glassFieldClass()}
                    value={g.roleLabel ?? ''}
                    onChange={(e) =>
                      updateGuardian(idx, { roleLabel: e.target.value })
                    }
                    placeholder="e.g. Uncle, Grandmother"
                  />
                </Field>
              )}
              <Field label="Full Name *" icon={User}>
                <input
                  className={glassFieldClass()}
                  value={g.fullName}
                  onChange={(e) =>
                    updateGuardian(idx, { fullName: e.target.value })
                  }
                />
              </Field>
              <Field label="National ID / Passport" icon={Shield}>
                <input
                  className={glassFieldClass()}
                  value={g.idNumber ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { idNumber: e.target.value })
                  }
                />
              </Field>
              <Field label="Occupation" icon={Briefcase}>
                <input
                  className={glassFieldClass()}
                  value={g.occupation ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { occupation: e.target.value })
                  }
                />
              </Field>
              <Field label="Employer" icon={Building2}>
                <input
                  className={glassFieldClass()}
                  value={g.employer ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { employer: e.target.value })
                  }
                />
              </Field>
              <Field label="Phone" icon={Phone}>
                <input
                  className={glassFieldClass()}
                  value={g.phone ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { phone: e.target.value })
                  }
                  placeholder="+267 7X XXX XXX"
                />
              </Field>
              <Field label="WhatsApp" icon={MessageCircle}>
                <input
                  className={glassFieldClass()}
                  value={g.whatsapp ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { whatsapp: e.target.value })
                  }
                  placeholder="+267 7X XXX XXX"
                />
              </Field>
              <Field label="Email" icon={Mail}>
                <input
                  type="email"
                  className={glassFieldClass()}
                  value={g.email ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { email: e.target.value })
                  }
                />
              </Field>
              <Field label="Address" icon={MapPin}>
                <input
                  className={glassFieldClass()}
                  value={g.address ?? ''}
                  onChange={(e) =>
                    updateGuardian(idx, { address: e.target.value })
                  }
                />
              </Field>
            </div>

            {/* Consent toggles */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={g.consentToSend ?? false}
                  onChange={(e) =>
                    updateGuardian(idx, { consentToSend: e.target.checked })
                  }
                  className="rounded border-gray-300 text-primaryLight focus:ring-primary"
                />
                Consent to receive reports via email
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={g.consentToWhatsapp ?? false}
                  onChange={(e) =>
                    updateGuardian(idx, { consentToWhatsapp: e.target.checked })
                  }
                  className="rounded border-gray-300 text-primaryLight focus:ring-primary"
                />
                Consent to WhatsApp messages
              </label>
            </div>
          </SoftCard>
        ))}

        <button
          onClick={addGuardian}
          className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl text-sm text-gray-500 dark:text-gray-400 hover:border-primaryLight hover:text-primaryLight transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          Add Another Guardian
        </button>
      </div>
    );
  }

  /* ---- MEDICAL TAB ---------------------------------------------- */

  function renderEntranceTab() {
    const subjectsSorted = [...subjects].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const usedSubjectIds = new Set(
      entrance.results.map((r) => r.subjectId).filter(Boolean),
    );

    function addRow() {
      setEntrance((e) => ({
        ...e,
        results: [...e.results, { subjectId: '', mark: '', outOf: '' }],
      }));
    }
    function updateRow(idx: number, patch: Partial<EntranceRow>) {
      setEntrance((e) => ({
        ...e,
        results: e.results.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
      }));
    }
    function removeRow(idx: number) {
      setEntrance((e) => ({
        ...e,
        results: e.results.filter((_, i) => i !== idx),
      }));
    }

    return (
      <SoftCard>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <ClipboardCheck size={20} className="text-primaryLight" />
          Entrance Test
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Record any entrance/placement test the pupil sat. Results appear in the pupil's History
          tab. Different pupils may sit different subjects.
        </p>

        <label className="flex items-center gap-3 cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={entrance.taken}
            onChange={(e) =>
              setEntrance((cur) => ({
                ...cur,
                taken: e.target.checked,
                results:
                  e.target.checked && cur.results.length === 0
                    ? [{ subjectId: '', mark: '', outOf: '' }]
                    : cur.results,
              }))
            }
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            This pupil sat an entrance test
          </span>
        </label>

        {entrance.taken && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Test date" icon={Calendar}>
                <input
                  type="date"
                  className={glassFieldClass()}
                  value={entrance.testDate}
                  onChange={(e) =>
                    setEntrance((cur) => ({ ...cur, testDate: e.target.value }))
                  }
                />
              </Field>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Subjects &amp; marks
                </span>
                <AccentButton icon={Plus} onClick={addRow}>
                  Add subject
                </AccentButton>
              </div>

              {entrance.results.length === 0 && (
                <p className="text-sm text-gray-500">
                  No subjects added yet — use “Add subject”.
                </p>
              )}

              <div className="space-y-2">
                {entrance.results.map((row, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <select
                      className={glassFieldClass('flex-1 min-w-[160px]')}
                      value={row.subjectId}
                      onChange={(e) => updateRow(idx, { subjectId: e.target.value })}
                    >
                      <option value="">— choose subject —</option>
                      {subjectsSorted.map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                          disabled={usedSubjectIds.has(s.id) && row.subjectId !== s.id}
                        >
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      inputMode="numeric"
                      className={glassFieldClass('w-24')}
                      placeholder="Mark"
                      value={row.mark}
                      onChange={(e) => updateRow(idx, { mark: e.target.value })}
                    />
                    <span className="text-gray-400 text-sm">/</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      className={glassFieldClass('w-24')}
                      placeholder="Out of"
                      value={row.outOf}
                      onChange={(e) => updateRow(idx, { outOf: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                “Out of” is optional — leave it blank to record a plain mark.
              </p>
            </div>

            <Field label="Notes" full>
              <textarea
                className={glassFieldClass('min-h-[80px]')}
                value={entrance.notes}
                onChange={(e) =>
                  setEntrance((cur) => ({ ...cur, notes: e.target.value }))
                }
                placeholder="Any remarks about the entrance test (optional)"
              />
            </Field>
          </div>
        )}
      </SoftCard>
    );
  }

  function renderMedicalTab() {
    return (
      <SoftCard>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
          <Stethoscope size={20} className="text-primaryLight" />
          Medical Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Doctor's Name" icon={Stethoscope}>
            <input
              className={glassFieldClass()}
              value={medical.doctorName}
              onChange={(e) =>
                setMedical((m) => ({ ...m, doctorName: e.target.value }))
              }
            />
          </Field>
          <Field label="Doctor's Phone" icon={Phone}>
            <input
              className={glassFieldClass()}
              value={medical.doctorPhone}
              onChange={(e) =>
                setMedical((m) => ({ ...m, doctorPhone: e.target.value }))
              }
            />
          </Field>
          <Field label="Medical Aid Provider">
            <input
              className={glassFieldClass()}
              value={medical.medicalAidProvider}
              onChange={(e) =>
                setMedical((m) => ({ ...m, medicalAidProvider: e.target.value }))
              }
              placeholder="e.g. BPOMAS, Bomaid, Pula Medical Aid"
            />
          </Field>
          <Field label="Medical Aid Number">
            <input
              className={glassFieldClass()}
              value={medical.medicalAidNumber}
              onChange={(e) =>
                setMedical((m) => ({ ...m, medicalAidNumber: e.target.value }))
              }
            />
          </Field>
          <Field label="Blood Type">
            <select
              className={glassFieldClass()}
              value={medical.bloodType}
              onChange={(e) =>
                setMedical((m) => ({ ...m, bloodType: e.target.value }))
              }
            >
              <option value="">Unknown</option>
              {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(
                (t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ),
              )}
            </select>
          </Field>
          <div /> {/* spacer for grid alignment */}
          <Field label="Allergies" icon={AlertCircle} full>
            <textarea
              className={glassFieldClass('min-h-[80px]')}
              value={medical.allergies}
              onChange={(e) =>
                setMedical((m) => ({ ...m, allergies: e.target.value }))
              }
              placeholder="List any known allergies…"
            />
          </Field>
          <Field label="Chronic Conditions" icon={Heart} full>
            <textarea
              className={glassFieldClass('min-h-[80px]')}
              value={medical.chronicConditions}
              onChange={(e) =>
                setMedical((m) => ({ ...m, chronicConditions: e.target.value }))
              }
              placeholder="e.g. Asthma, diabetes, epilepsy…"
            />
          </Field>
          <Field label="Current Medications" full>
            <textarea
              className={glassFieldClass('min-h-[80px]')}
              value={medical.medications}
              onChange={(e) =>
                setMedical((m) => ({ ...m, medications: e.target.value }))
              }
              placeholder="List any medications the student takes regularly…"
            />
          </Field>
        </div>
      </SoftCard>
    );
  }

  /* ---- ADMINISTRATIVE TAB --------------------------------------- */

  function renderAdminTab() {
    return (
      <SoftCard>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
          <ClipboardList size={20} className="text-primaryLight" />
          Administrative Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Previous School" icon={Building2}>
            <input
              className={glassFieldClass()}
              value={admin.previousSchool}
              onChange={(e) =>
                setAdmin((a) => ({ ...a, previousSchool: e.target.value }))
              }
            />
          </Field>
          <Field label="Date of Admission" icon={Calendar}>
            <input
              type="date"
              className={glassFieldClass()}
              value={admin.dateOfAdmission}
              onChange={(e) =>
                setAdmin((a) => ({ ...a, dateOfAdmission: e.target.value }))
              }
            />
          </Field>
          <Field label="Boarding Status" icon={Home}>
            <select
              className={glassFieldClass()}
              value={admin.boardingStatus}
              onChange={(e) =>
                setAdmin((a) => ({
                  ...a,
                  boardingStatus: e.target.value as 'day' | 'boarding',
                }))
              }
            >
              {BOARDING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <div /> {/* spacer */}
          <Field label="Additional Notes" full>
            <textarea
              className={glassFieldClass('min-h-[100px]')}
              value={admin.notes}
              onChange={(e) =>
                setAdmin((a) => ({ ...a, notes: e.target.value }))
              }
              placeholder="Any other notes about this admission…"
            />
          </Field>
        </div>
      </SoftCard>
    );
  }
}

/* ==================================================================
   FIELD WRAPPER
   ================================================================== */

function Field({
  label,
  icon: Icon,
  children,
  full,
}: {
  label: string;
  icon?: typeof User;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={14} className="text-gray-400" />}
        {label}
      </label>
      {children}
    </div>
  );
}
